/**
 * Выбор движения — с поиском и возможностью завести своё.
 *
 * Раньше жил внутри экрана «Правка тренировки» и умел одно: отфильтровать
 * подстрокой сорок каталожных движений. Этого хватало, пока в каталоге было
 * всё, что человек делает. Но работы на предплечья там нет вообще — ни
 * упражнения, ни мышечной группы, — и поиск честно отвечал «ничего не
 * нашлось», после чего человеку было некуда деться.
 *
 * Поэтому пустой результат здесь — не тупик, а развилка: тем же текстом, что
 * человек уже напечатал, предлагается завести своё движение.
 *
 * ПРО КЛАВИАТУРУ (раздел 13). Текстовое поле в вебвью Telegram на iOS
 * перекрывает пол-экрана, и между подходами это дорого. Цена принята
 * осознанно и минимизирована: поле одно, оно открывается только по явному
 * тапу, название в форму подставляется уже напечатанное, а мышечная группа и
 * снаряд выбираются тапами по чипам — без клавиатуры вообще.
 */

import { useState } from 'react'

import { archiveExercise, createExercise, type Catalog } from '../api/exercises'
import {
  BAR_CHOICES,
  DEFAULT_BAR,
  EQUIPMENT_KINDS,
  MUSCLE_GROUPS,
  type Exercise,
} from '../api/types'
import { equipmentLabel, muscleLabel } from '../labels'
import { ConfirmButton, Loader } from './ui'

/**
 * Сколько строк показываем сразу. Список прокручивается внутри карточки
 * (`.se-list` с ограниченной высотой), поэтому резать его коротко незачем:
 * на экране планирования человек листает справочник целиком.
 */
const PICKER_LIMIT = 40

/** Группа по умолчанию в форме — та, ради которой всё и затевалось. */
const DEFAULT_GROUP = 'forearms'
const DEFAULT_EQUIPMENT = 'dumbbell'

function errorText(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown })?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  return error instanceof Error && error.message ? error.message : fallback
}

export function ExercisePicker({
  catalog,
  taken,
  onPick,
  disabled = false,
  allowCreate = true,
}: {
  catalog: Catalog
  /** Движения, уже стоящие в тренировке: сервер отверг бы их с 409. */
  taken: Set<number>
  onPick: (exercise: Exercise) => void
  disabled?: boolean
  allowCreate?: boolean
}) {
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [dropError, setDropError] = useState('')

  const needle = query.trim().toLowerCase()
  const pool = catalog.selectable
  const found = needle ? pool.filter((e) => e.name.toLowerCase().includes(needle)) : pool
  const shown = found.slice(0, PICKER_LIMIT)

  if (catalog.loading && catalog.list.length === 0) {
    return <Loader label="Загружаю справочник…" />
  }

  if (creating) {
    return (
      <NewExerciseForm
        initialName={query.trim()}
        onCancel={() => setCreating(false)}
        onCreated={(exercise) => {
          setCreating(false)
          onPick(exercise)
        }}
      />
    )
  }

  return (
    <div>
      <input
        className="se-search"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Название движения: жим, тяга, присед…"
        autoComplete="off"
        spellCheck={false}
      />

      {shown.length === 0 ? (
        <div className="faint">
          {catalog.list.length === 0
            ? 'Справочник не загрузился — выбрать движение не из чего. Проверь связь и обнови экран.'
            : 'Ничего не нашлось. Попробуй короче: «жим», «тяга» — или заведи своё.'}
        </div>
      ) : (
        <div className="se-list">
          {shown.map((ex) => {
            const busyOrTaken = disabled || taken.has(ex.id)
            return (
              <div key={ex.id} className="se-row">
                <button
                  type="button"
                  className="se-pick"
                  disabled={busyOrTaken}
                  onClick={() => onPick(ex)}
                >
                  <span>{ex.name}</span>
                  {taken.has(ex.id) ? (
                    <span className="pill">уже в тренировке</span>
                  ) : (
                    ex.is_custom && <span className="pill">своё</span>
                  )}
                </button>
                {/* Убрать можно только своё. Опечатка вроде «Молоки» иначе
                    осталась бы в списке навсегда и мозолила бы глаза при
                    каждом выборе. Это не удаление: подходы по движению
                    остаются, и «История» продолжит называть их словами. */}
                {ex.is_custom && (
                  <ConfirmButton
                    className="btn ghost se-drop"
                    confirmLabel="Убрать?"
                    onConfirm={() => {
                      setDropError('')
                      void archiveExercise(ex.id).catch((e: unknown) => {
                        // Молчаливый отказ здесь хуже всего: строка осталась
                        // на месте, и человек решит, что промахнулся, и
                        // нажмёт ещё раз.
                        setDropError(errorText(e, `Не удалось убрать «${ex.name}»`))
                      })
                    }}
                  >
                    ×
                  </ConfirmButton>
                )}
              </div>
            )
          })}
        </div>
      )}

      {dropError && <div className="se-fail">{dropError}</div>}

      {found.length > shown.length && (
        <div className="faint" style={{ marginTop: 8 }}>
          Показаны первые {shown.length} из {found.length} — уточни запрос.
        </div>
      )}

      {allowCreate && (
        <button
          className="btn wide ghost"
          style={{ marginTop: 10 }}
          disabled={disabled}
          onClick={() => setCreating(true)}
        >
          {needle ? `Завести своё: «${query.trim()}»` : 'Завести своё движение'}
        </button>
      )}
    </div>
  )
}

/**
 * Форма своего движения: название, что качает, на чём.
 *
 * Три поля, и ни одного лишнего. Шаг веса, минимальный вес и «вес на одну
 * сторону» не спрашиваются: их выводит сервер из снаряда по соглашениям
 * каталога. Спросить это у человека значит задать три вопроса про числа,
 * которых он не знает.
 */
function NewExerciseForm({
  initialName,
  onCreated,
  onCancel,
}: {
  initialName: string
  onCreated: (exercise: Exercise) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initialName)
  const [group, setGroup] = useState<string>(DEFAULT_GROUP)
  const [equipment, setEquipment] = useState<string>(DEFAULT_EQUIPMENT)
  const [compound, setCompound] = useState(false)
  const [bar, setBar] = useState<number>(DEFAULT_BAR)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const clean = name.trim()
  const tooShort = clean.length < 2

  async function submit(): Promise<void> {
    setBusy(true)
    setFailure('')
    try {
      const created = await createExercise({
        name: clean,
        muscle_group: group,
        equipment,
        is_compound: compound,
        // Только для штанги: у гантели «гриф» это первая гантель, и чужое
        // число там сломало бы округление рабочих весов.
        bar_weight: equipment === 'barbell' ? bar : null,
      })
      onCreated(created)
    } catch (e) {
      setFailure(errorText(e, 'Не удалось завести движение'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <input
        className="se-search"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Как называешь: молотки, сгибания на предплечья…"
        autoComplete="off"
        spellCheck={false}
        maxLength={128}
      />

      <div className="xf-label">Что качает</div>
      <div className="xf-chips">
        {MUSCLE_GROUPS.map((m) => (
          <button
            key={m}
            type="button"
            className={m === group ? 'chip on' : 'chip'}
            onClick={() => setGroup(m)}
          >
            {muscleLabel(m)}
          </button>
        ))}
      </div>

      <div className="xf-label">На чём</div>
      <div className="xf-chips">
        {EQUIPMENT_KINDS.map((k) => (
          <button
            key={k}
            type="button"
            className={k === equipment ? 'chip on' : 'chip'}
            onClick={() => setEquipment(k)}
          >
            {equipmentLabel(k)}
          </button>
        ))}
      </div>

      {/* Гриф спрашивается только у штанги и только потому, что от него
          зависит и минимальный возможный вес, и то, как считаются блины на
          экране тренировки. Сменить его потом можно там же, одним тапом. */}
      {equipment === 'barbell' && (
        <>
          <div className="xf-label">Вес пустого грифа</div>
          <div className="xf-chips">
            {BAR_CHOICES.map((kg) => (
              <button
                key={kg}
                type="button"
                className={kg === bar ? 'chip on' : 'chip'}
                onClick={() => setBar(kg)}
              >
                {kg} кг
              </button>
            ))}
          </div>
        </>
      )}

      {/* Влияет ровно на одно: базовое движение отдыхает дольше (раздел 7). */}
      <button
        type="button"
        className={compound ? 'chip on xf-wide' : 'chip xf-wide'}
        onClick={() => setCompound((v) => !v)}
      >
        {compound ? '✓ ' : ''}Базовое движение — отдых дольше
      </button>

      <div className="faint" style={{ marginTop: 10 }}>
        {equipment === 'bodyweight'
          ? 'Свой вес: в подход пиши только дополнительное отягощение, 0 — без него.'
          : equipment === 'dumbbell'
            ? 'Гантели: вес пишется на одну руку, шаг 2 кг.'
            : equipment === 'barbell'
              ? 'Штанга: на тренировке останется набрать блины, гриф прибавится сам.'
              : 'Шаг веса подставит сервер — по тому, что реально можно собрать.'}
      </div>
      <div className="faint" style={{ marginTop: 6 }}>
        Вес на сегодня движок назначит после первых подходов: пока истории нет,
        придумывать его нельзя.
      </div>

      {failure && <div className="se-fail">{failure}</div>}

      <button
        className="btn wide primary"
        style={{ marginTop: 10 }}
        disabled={busy || tooShort}
        onClick={() => void submit()}
      >
        {busy ? 'Завожу…' : 'Завести и добавить'}
      </button>
      <button className="btn wide ghost" style={{ marginTop: 8 }} onClick={onCancel}>
        Назад к поиску
      </button>
    </div>
  )
}

export default ExercisePicker
